import React, { createContext, useState, useContext, useEffect } from 'react'
import axios from 'axios'

import { ClientWithoutRole, UserContextProps, UserProviderProps, UserWithoutRole } from './types'
import { apiUrl } from '../../globals'
import { AuthContext } from '../Auth/AuthContext'

const defaultUser = {
  id: 0,
  firstName: '',
  lastName: '',
  email: '',
  role: 'participant' as const,
}

export const UserContext = createContext<UserContextProps>({
  userDetails: defaultUser,
  getUserDetails: () => Promise.resolve(defaultUser),
  changeProfilePicture: () => Promise.resolve(''),
  changeUserDetails: () => Promise.resolve(),
})

export const UserProvider = ({ children }: UserProviderProps) => {
  const { authUser } = useContext(AuthContext)
  const [ userDetails, setUserDetails ] = useState<UserWithoutRole>(defaultUser)

  const getUserDetails = (): Promise<UserWithoutRole> => {
    const response = axios.get(`${apiUrl}/users/${authUser.id}`)
      .then((res) => {
        setUserDetails(res.data)
        return res.data
      })
      .catch((err) => console.log("Error getting user details: ", err))
    
    return response
  }

  const changeProfilePicture = async (base64ImageUrl: string): Promise<string> => {
    return axios
      .patch(`${apiUrl}/users/${authUser.id}/profile-picture`, {
        profilePicture: base64ImageUrl,
      })
      .then(() => {
        setUserDetails((prevUserDetails) => ({
          ...prevUserDetails,
          profilePicture: base64ImageUrl,
        }))
  
        return base64ImageUrl
      })
      .catch((err) => {
        console.error("Error changing profile picture: ", err)
        throw err
      })
  }

  const changeUserDetails = async (userDetails: UserWithoutRole | ClientWithoutRole): Promise<void> => {
    return axios
      .patch(`${apiUrl}/users/${authUser.id}`, userDetails)
      .then((res) => {
        setUserDetails(res.data)
      })
      .catch((err) => {
        console.error("Error changing user details: ", err)
        throw err
      })
  }

  useEffect(() => {
    if (authUser.id) {
      getUserDetails()
    }
  }, [ authUser.id ])

  return (
    <UserContext.Provider value={{
      userDetails,
      getUserDetails,
      changeProfilePicture,
      changeUserDetails,
    }}>
      {children}
    </UserContext.Provider>
  )
}

